import React from "react";
import { fmt, computeRowTotals } from "./PdfQuotation";

const LOGO1_PATH = "/logo.jpeg";

/* ══════════════════════════════════════════════════════════════════════
   QuotationDocumentPreview
   Read-only, styled rendering of a quotation — the on-screen counterpart
   of quotationPdf.js's buildQuotationPDF, kept visually in sync with it.
   Used by:
   - QuotationModal.jsx  → right after creating a quotation
   - LeadTable.jsx        → "View Quotation" on any lead that has one

   Props:
   - branchName   : shown instead of a hardcoded Client Name
   - quotationNumber
   - customer     : { companyName, address, pinCode }
   - rows         : [{ category, item, qty, rate, gstPct|gst }]
   - subject
   - employeeName
   - employeePhone
   - terms        : array of term strings (user-defined, no hardcoded defaults)
══════════════════════════════════════════════════════════════════════ */
const QuotationDocumentPreview = ({
  branchName,
  quotationNumber,
  customer,
  rows = [],
  subject,
  employeeName,
  employeePhone,
  terms = [],
}) => {
  const computedRows = rows.map((r) => ({ ...r, ...computeRowTotals(r) }));
  const grandTotal = computedRows.reduce((s, r) => s + r.inclTax, 0);
  const cleanTerms = (terms || []).map((t) => (t || "").trim()).filter(Boolean);

  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
      <div className="px-6 py-4" style={{ background: "rgb(30,18,60)" }}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-white font-bold text-sm m-0">{branchName || "SAJ INFO Pvt. Ltd."}</p>
            <p className="text-slate-400 text-xs mt-1 m-0">SN Mallick Rd, near Hooghly Rural Police Line, Singur - 712409</p>
            <p className="text-slate-400 text-xs m-0">CIN: 00000000</p>
          </div>
          <img src={LOGO1_PATH} alt="Logo" className="h-12 w-auto object-contain bg-white rounded p-0.5"
            onError={(e) => { e.target.style.display = "none"; }} />
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-xs text-slate-400 m-0">To.</p>
            <p className="text-sm font-bold text-slate-800 m-0 mt-1">{customer?.companyName || "—"}</p>
            <p className="text-xs text-slate-500 m-0">{customer?.address}</p>
            {customer?.pinCode && (
              <p className="text-xs text-slate-500 m-0">Pin: {customer.pinCode}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">
              Date: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" })}
            </p>
            {quotationNumber && (
              <p className="text-xs font-bold text-blue-700 mt-1">Quot. No: {quotationNumber}</p>
            )}
          </div>
        </div>

        <p className="text-xs font-bold text-slate-700 border-b border-slate-200 pb-2 mb-3">
          Sub: {subject || "—"}
        </p>
        <p className="text-xs text-slate-500 mb-4">
          Dear Sir,<br />
          We are pleased to submit the price proposal for the below items.
          The pricing is quoted in INR only with a validity of 7 days from the date of offer.
        </p>

        <table className="w-full text-xs border border-slate-200 overflow-hidden mb-4 border-collapse">
          <thead>
            <tr style={{ background: "rgb(30,18,60)" }}>
              {["Category", "Item", "Qty", "Rate", "GST %", "Amount (Incl. Tax)"].map((h) => (
                <th key={h} className="text-white px-3 py-2 text-center text-[10px] font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {computedRows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="px-3 py-1.5 text-slate-600">{r.category || "—"}</td>
                <td className="px-3 py-1.5 text-slate-700">{r.item || "—"}</td>
                <td className="px-3 py-1.5 text-center text-slate-600">{fmt(r.qty)}</td>
                <td className="px-3 py-1.5 text-center text-slate-600">₹{fmt(r.rate)}</td>
                <td className="px-3 py-1.5 text-center text-slate-600">{fmt(r.gst)}%</td>
                <td className="px-3 py-1.5 text-center text-slate-600">₹{fmt(r.inclTax)}</td>
              </tr>
            ))}
            <tr className="bg-slate-50">
              <td colSpan={5} className="px-3 py-2 text-right text-xs font-bold text-slate-600">Total</td>
              <td className="px-3 py-2 text-center font-bold text-blue-800">₹{fmt(grandTotal)}</td>
            </tr>
          </tbody>
        </table>

        {cleanTerms.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-bold text-slate-700 mb-1">Terms and Condition:</p>
            {cleanTerms.map((t, i) => (
              <p key={i} className="text-xs text-slate-500 m-0">• {t}</p>
            ))}
          </div>
        )}

        <div className="mb-4">
          <p className="text-xs font-bold text-slate-700 mb-1">Please Find The Account Details Below-</p>
          {[
            ["Account Number:", "0000111222111"],
            ["IFSC:",           "Demo0000000"],
            ["Bank Name:",      "Demo"],
            ["Branch:",         "Hooghly"],
            ["Account Type:",   "Current"],
            ["From:",           branchName || "SAJ INFO Pvt. Ltd."],
          ].map(([k, v]) => (
            <div key={k} className="flex gap-2 text-xs">
              <span className="font-semibold text-slate-600 w-28">{k}</span>
              <span className="text-slate-500">{v}</span>
            </div>
          ))}
        </div>

        <div>
          <p className="text-xs font-bold text-slate-700 m-0">{employeeName || "—"}</p>
          {employeePhone && <p className="text-xs text-slate-500 m-0">{employeePhone}</p>}
        </div>
      </div>

      <div className="px-6 py-3 border-t border-purple-200 bg-purple-50 text-center">
        <p className="text-[10px] text-slate-500 m-0">Registered Office: SN Mallick Rd, near Hooghly Rural Police Line, Nabapally, Daluigacha, Singur, Balarambati, West Bengal 712409 | CIN: 0000000000</p>
        <p className="text-[10px] text-slate-500 m-0">Mail: info@sajinfoworld.com | PHONE:099032 14793 | www.sajinfoworld.com</p>
      </div>
    </div>
  );
};

export default QuotationDocumentPreview;